import React, { useState } from "react";
import dayjs from "dayjs";

const CHART_COLORS = [
  "#4e79a7",
  "#f28e2b",
  "#e15759",
  "#76b7b2",
  "#59a14f",
  "#edc948",
  "#b07aa1",
  "#ff9da7",
  "#9c755f",
  "#bab0ab",
];

function renderShareBars(rows) {
  return rows.map((row, idx) => {
    const count = Number(row.count) || 0;
    const pct = Number.isFinite(row.pct) ? row.pct : 0;
    const label = row.label || "—";
    const color = row.color || CHART_COLORS[0];
    return (
      <div
        key={row.key != null ? row.key : `${label}-${idx}`}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "6px",
        }}
      >
        <div
          style={{
            width: row.labelWidth || "140px",
            fontSize: "12px",
            color: "#495057",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={label}
        >
          {label}
        </div>
        <div
          style={{
            flex: 1,
            height: "18px",
            background: "#f1f3f5",
            borderRadius: "999px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${Math.min(100, Math.max(0, pct))}%`,
              height: "100%",
              background: color,
              borderRadius: "999px",
              minWidth: count > 0 ? "4px" : 0,
              transition: "width 0.3s ease",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: "6px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(255,255,255,0.9)",
              borderRadius: "12px",
              padding: "0 6px",
              fontSize: "11px",
              color: "#343a40",
              boxShadow: "0 0 0 1px #e9ecef",
            }}
          >
            {pct.toFixed(2)}% ({count})
          </div>
        </div>
      </div>
    );
  });
}

const TYPE_LABELS = {
  single_choice: "Multiple Choice (Single Select)",
  multi_choice: "Multiple Choice (Multi-Select)",
  rating: "Rating Scale",
  likert: "Likert Scale",
  nps: "NPS (0–10)",
  open_ended: "Open-Ended (Short Text)",
};

/**
 * Presentational component for detailed survey results (charts, KPIs per question).
 * Used inside Survey Details → Results tab. Receives data as props.
 */
function SurveyResultsContent({ survey, results, totalResponses, loading, error, embedded }) {
  const [questionFilter, setQuestionFilter] = useState([]);
  const [sentimentFilterByQuestionId, setSentimentFilterByQuestionId] = useState({});

  // 1. Multiple Choice (Single Select)
  function renderSingleChoiceKpis(r) {
    const counts = r.option_counts || {};
    const questionOptions = Array.isArray(r.options)
      ? r.options.filter((o) => o != null && String(o).trim() !== "")
      : [];
    const optionsToList = questionOptions.length
      ? questionOptions.map((o) => String(o).trim())
      : Object.keys(counts);
    const total = r.total_answers || 0;
    if (!optionsToList.length) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No options defined.</p>;
    }
    const entriesWithZeros = optionsToList.map((opt) => [opt, counts[opt] || 0]);
    const sorted = [...entriesWithZeros].sort((a, b) => (b[1] || 0) - (a[1] || 0));
    const most = sorted[0];
    const least = sorted[sorted.length - 1];
    const primaryColor = CHART_COLORS[0];
    const secondaryColor = "#f1c40f";

    const answered = total;
    const denominator = totalResponses || answered;
    const ratio = denominator > 0 ? Math.min(1, answered / denominator) : 0;
    const gaugeBackground =
      denominator > 0 && answered > 0
        ? `linear-gradient(to right, ${primaryColor} 0 ${ratio * 100}%, #e9ecef ${ratio * 100}% 100%)`
        : "#f1f3f5";

    const topLabel = most ? most[0] || "—" : "—";
    const topCount = most ? most[1] || 0 : 0;
    const topPct = total > 0 ? (topCount / total) * 100 : 0;

    const donutBackground =
      total > 0 && topCount > 0
        ? `conic-gradient(${secondaryColor} 0 ${topPct}%, #e9ecef ${topPct}% 100%)`
        : "#f1f3f5";

    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            marginBottom: "10px",
            fontSize: "12px",
            color: "#212529",
          }}
        >
          <div
            style={{
              flex: "1 1 220px",
              background: "#f8f9fa",
              borderRadius: "8px",
              padding: "8px 10px",
              border: "1px solid #e9ecef",
            }}
          >
            <div style={{ color: "#6c757d", marginBottom: "2px" }}>Most Selected Topic</div>
            <div style={{ fontWeight: 600 }}>{topLabel}</div>
            <div style={{ color: "#6c757d", marginTop: "2px" }}>
              {topCount} response{topCount === 1 ? "" : "s"}
            </div>
          </div>
          <div
            style={{
              flex: "0 0 170px",
              background: "#f8f9fa",
              borderRadius: "8px",
              padding: "8px 10px",
              border: "1px solid #e9ecef",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <div>
              <div style={{ color: "#6c757d", marginBottom: "2px" }}>Responses to question</div>
              <div style={{ fontWeight: 600 }}>
                {answered} / {denominator || "—"}
              </div>
            </div>
            <div
              style={{
                width: "60px",
                height: "22px",
                borderRadius: "999px",
                background: gaugeBackground,
                boxShadow: "0 0 0 1px #dee2e6 inset",
              }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div
            style={{
              flex: "0 0 220px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
            }}
          >
            <div
              style={{
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                background: donutBackground,
                position: "relative",
                boxShadow: "0 0 0 1px #e9ecef inset",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "88px",
                  height: "88px",
                  borderRadius: "50%",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  color: "#495057",
                  boxShadow: "0 0 0 1px #f1f3f5",
                  textAlign: "center",
                  padding: "4px",
                }}
              >
                <div style={{ fontSize: "16px", fontWeight: 600 }}>
                  {topPct.toFixed(0)}%
                </div>
                <div style={{ fontSize: "11px" }}>
                  ({topCount} response{topCount === 1 ? "" : "s"})
                </div>
              </div>
            </div>
            {topLabel && (
              <div style={{ fontSize: "13px", color: "#212529" }}>{topLabel}</div>
            )}
          </div>

          <div
            style={{
              flex: "1 1 220px",
              fontSize: "12px",
              color: "#212529",
              minWidth: "200px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "6px",
              }}
            >
              <div style={{ fontWeight: 600 }}>Answers collected: {total}</div>
              {least && (
                <div style={{ fontSize: "11px", color: "#6c757d" }}>
                  Least selected: {least[0] || "—"}
                </div>
              )}
            </div>
            {renderShareBars(
              entriesWithZeros.map(([opt, count], idx) => ({
                key: opt || idx,
                label: opt || "—",
                count,
                pct: total > 0 ? (count / total) * 100 : 0,
                color: CHART_COLORS[idx % CHART_COLORS.length],
              }))
            )}
          </div>
        </div>
      </div>
    );
  }

  // 2. Multiple Choice (Multi-Select)
  function renderMultiChoiceKpis(r) {
    const respondentCount = r.option_respondent_count || {};
    const questionOptions = Array.isArray(r.options)
      ? r.options.filter((o) => o != null && String(o).trim() !== "")
      : [];
    const optionsToList = questionOptions.length
      ? questionOptions.map((o) => String(o).trim())
      : Object.keys(respondentCount);
    const total = r.total_answers || 0;
    const primaryColor = CHART_COLORS[0];

    if (!optionsToList.length) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No options defined.</p>;
    }
    const entries = optionsToList.map((opt) => [opt, respondentCount[opt] || 0]);
    const anyResponses = entries.some(([, c]) => c > 0);

    const answered = total;
    const denominator = totalResponses || answered;
    const ratio = denominator > 0 ? Math.min(1, answered / denominator) : 0;
    const ringBackground =
      denominator > 0 && answered > 0
        ? `conic-gradient(${primaryColor} 0 ${ratio * 100}%, #e9ecef ${ratio * 100}% 100%)`
        : "#f1f3f5";

    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: "1 1 260px" }}>
            <div style={{ marginBottom: "6px", fontSize: "12px", color: "#6c757d" }}>
              % of respondents selecting each option
            </div>
            {renderShareBars(
              entries.map(([opt, count]) => ({
                key: opt,
                label: opt || "—",
                count,
                pct: total > 0 ? (count / total) * 100 : 0,
                color: primaryColor,
              }))
            )}
            {!anyResponses && (
              <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "4px" }}>
                No responses yet for these options.
              </div>
            )}
          </div>

          <div
            style={{
              flex: "0 0 170px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              color: "#495057",
              gap: "6px",
            }}
          >
            <div style={{ fontSize: "11px", color: "#6c757d" }}>Answers collected</div>
            <div
              style={{
                width: "90px",
                height: "90px",
                borderRadius: "50%",
                background: ringBackground,
                position: "relative",
                boxShadow: "0 0 0 1px #e9ecef inset",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "54px",
                  height: "54px",
                  borderRadius: "50%",
                  background: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "11px",
                  color: "#495057",
                  boxShadow: "0 0 0 1px #f1f3f5",
                }}
              >
                <div style={{ fontSize: "13px", fontWeight: 600 }}>
                  {answered}
                  {denominator ? ` / ${denominator}` : ""}
                </div>
                <div>Responded</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 3. Rating Scale
  function renderRatingKpis(r) {
    const ns = r.numeric_stats;
    const valueCounts = r.value_counts || {};
    if (!ns || !ns.count) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No numeric responses yet.</p>;
    }
    const scaleMin = r.scale_min != null ? r.scale_min : 1;
    const scaleMax = r.scale_max != null ? r.scale_max : 10;
    const labels = [];
    for (let v = scaleMax; v >= scaleMin; v--) labels.push(v);

    const totalNumeric = ns.count;

    const average = ns.average != null ? ns.average : null;
    const starsOutOf = scaleMax - scaleMin + 1;
    const normalizedAvg =
      average != null ? ((average - scaleMin) / (scaleMax - scaleMin || 1)) * starsOutOf : null;
    const fullStars = normalizedAvg != null ? Math.floor(normalizedAvg) : 0;
    const halfStar = normalizedAvg != null ? normalizedAvg - fullStars >= 0.5 : false;
    const emptyStars =
      normalizedAvg != null ? Math.max(0, starsOutOf - fullStars - (halfStar ? 1 : 0)) : starsOutOf;

    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              fontSize: "13px",
              color: "#212529",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: "12px", color: "#6c757d" }}>Average</div>
              <div style={{ fontWeight: 600 }}>
                {average != null ? average : "—"}
                {scaleMax ? ` / ${scaleMax}` : ""}
              </div>
              {average != null && (
                <div style={{ fontSize: "11px", color: "#6c757d", marginTop: "2px" }}>
                  {average.toFixed(1)} out of {scaleMax} stars{" "}
                  <span aria-hidden="true">
                    {Array.from({ length: fullStars }).map((_, i) => (
                      <span key={`full-${i}`} style={{ color: "#f5b301" }}>
                        ★
                      </span>
                    ))}
                    {halfStar && <span style={{ color: "#f5b301" }}>☆</span>}
                    {Array.from({ length: emptyStars }).map((_, i) => (
                      <span key={`empty-${i}`} style={{ color: "#ced4da" }}>
                        ☆
                      </span>
                    ))}
                  </span>
                </div>
              )}
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6c757d" }}>Median</div>
              <div style={{ fontWeight: 600 }}>
                {ns.median != null ? ns.median : "—"}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", color: "#6c757d" }}>Standard deviation</div>
              <div style={{ fontWeight: 600 }}>
                {ns.std_dev != null ? ns.std_dev : "—"}
              </div>
            </div>
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#212529",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 8px",
              borderRadius: "999px",
              background: "#f1f3f5",
              border: "1px solid #e9ecef",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                display: "inline-block",
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: "#0d6efd",
              }}
            />
            <span>
              Answers collected: <strong>{totalNumeric}</strong>
            </span>
          </div>
        </div>

        {renderShareBars(
          labels.map((v) => {
            const count = Number(valueCounts[v] || 0);
            const pct = totalNumeric > 0 ? (count / totalNumeric) * 100 : 0;
            return {
              key: v,
              label: `${v} ${Number(v) === 1 ? "star" : "stars"}`,
              count,
              pct,
            };
          })
        )}
      </div>
    );
  }

  // 4. Likert Scale
  function renderLikertKpis(r) {
    const counts = r.option_counts || {};
    const total = r.total_answers || 0;
    if (total === 0) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No responses yet.</p>;
    }
    const order = ["Strongly Agree", "Agree", "Neutral", "Disagree", "Strongly Disagree"];
    const questionOptions = Array.isArray(r.options)
      ? r.options.filter((o) => o != null && String(o).trim() !== "")
      : [];
    const orderIdx = (opt) => {
      const o = (opt || "").trim().toLowerCase();
      const i = order.findIndex((l) => l.toLowerCase() === o);
      return i >= 0 ? i : order.length;
    };

    let baseOptions;
    if (questionOptions.length) {
      baseOptions = questionOptions.map((o) => String(o).trim());
    } else {
      const keys = Object.keys(counts);
      const union = new Set([...order, ...keys]);
      baseOptions = Array.from(union);
    }

    const sortedOptions = [...baseOptions].sort(
      (a, b) => orderIdx(a) - orderIdx(b) || String(a).localeCompare(b)
    );

    const rows = sortedOptions.map((opt) => {
      const count = counts[opt] || 0;
      const pct = total > 0 ? Math.round((count * 10000) / total) / 100 : 0;
      return { label: opt || "—", count, pct };
    });

    // Classify each Likert option by exact category so "Disagree" / "Strongly Disagree"
    // are not mistaken for positive (they contain the substring "agree").
    const sentimentOf = (label) => {
      const t = (label || "").trim().toLowerCase();
      if (t === "strongly agree" || t === "agree") return "positive";
      if (t === "neutral") return "neutral";
      if (t === "disagree" || t === "strongly disagree") return "negative";
      return "other";
    };
    const sentimentColors = {
      positive: "var(--sentiment-positive)",
      neutral: "var(--sentiment-neutral)",
      negative: "var(--sentiment-negative)",
      other: "var(--sentiment-neutral)",
    };

    const topSentimentPct = rows
      .filter((row) => sentimentOf(row.label) === "positive")
      .reduce((sum, row) => sum + row.pct, 0);

    return (
      <div style={{ marginTop: "6px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "8px",
            fontSize: "12px",
            color: "#212529",
            flexWrap: "wrap",
            gap: "8px",
          }}
        >
          <div>
            Responses collected: <strong>{total}</strong>
          </div>
          {topSentimentPct > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <span style={{ fontSize: "11px", color: "#6c757d" }}>Top sentiment</span>
              <div
                style={{
                  padding: "4px 10px",
                  borderRadius: "999px",
                  background: "#198754",
                  color: "#fff",
                  fontSize: "11px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span
                  style={{
                    padding: "2px 6px",
                    borderRadius: "999px",
                    background: "rgba(255,255,255,0.2)",
                    fontWeight: 600,
                  }}
                >
                  {topSentimentPct.toFixed(0)}%
                </span>
                <span>Total Agree</span>
              </div>
            </div>
          )}
        </div>

        {renderShareBars(
          rows.map((row) => ({
            key: row.label,
            label: row.label,
            count: row.count,
            pct: row.pct,
            color: sentimentColors[sentimentOf(row.label)],
            labelWidth: "168px",
          }))
        )}
      </div>
    );
  }

  // 5. NPS
  function renderNpsKpis(r) {
    const nb = r.nps_breakdown;
    if (!nb || r.total_answers === 0) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No NPS responses yet.</p>;
    }

    const total = r.total_answers || 0;
    const npsValue = typeof nb.nps === "number" ? nb.nps : 0;
    const clamped = Math.max(-100, Math.min(100, npsValue));
    const angle = -90 + ((clamped + 100) / 200) * 180;

    const promotersPct = nb.promoters_pct || 0;
    const passivesPct = nb.passives_pct || 0;
    const detractorsPct = nb.detractors_pct || 0;

    const green = "#28a745";
    const red = "#dc3545";
    const grey = "#adb5bd";

    const labelPositions = [
      { value: -100, left: "4%", top: "65%" },
      { value: -50, left: "18%", top: "38%" },
      { value: -10, left: "36%", top: "20%" },
      { value: 0, left: "50%", top: "14%" },
      { value: 10, left: "64%", top: "20%" },
      { value: 50, left: "82%", top: "38%" },
      { value: 100, left: "96%", top: "65%" },
    ];

    return (
      <div style={{ marginTop: "8px" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            alignItems: "stretch",
          }}
        >
          <div style={{ flex: "1 1 260px", minWidth: "260px" }}>
            <div
              style={{
                borderRadius: "12px",
                border: "1px solid #e9ecef",
                padding: "10px 14px 12px",
                background: "#f8f9fa",
              }}
            >
              <div
                style={{
                  width: "180px",
                  height: "90px",
                  borderRadius: "180px 180px 0 0",
                  margin: "0 auto",
                  position: "relative",
                  overflow: "hidden",
                  background: "#ffffff",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    width: "180px",
                    height: "180px",
                    borderRadius: "50%",
                    left: "50%",
                    bottom: "-90px",
                    transform: "translateX(-50%)",
                    background:
                      "conic-gradient(from 180deg, " +
                      `${red} 0deg, ${red} 45deg, #ffc107 90deg, ${green} 135deg, ${green} 180deg)`,
                    boxShadow: "0 0 3px rgba(0,0,0,0.15)",
                    zIndex: 0,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    width: "130px",
                    height: "130px",
                    borderRadius: "50%",
                    left: "50%",
                    bottom: "-65px",
                    transform: "translateX(-50%)",
                    background: "#ffffff",
                    boxShadow: "0 0 0 1px rgba(255,255,255,0.9)",
                    zIndex: 1,
                  }}
                />
                {labelPositions.map((t) => (
                  <div
                    key={t.value}
                    style={{
                      position: "absolute",
                      left: t.left,
                      top: t.top,
                      transform: "translate(-50%, -50%)",
                      fontSize: "10px",
                      color: "#555",
                      zIndex: 2,
                    }}
                  >
                    {t.value > 0 ? `+${t.value}` : t.value}
                  </div>
                ))}
                <div
                  style={{
                    position: "absolute",
                    bottom: "-10px",
                    left: "50%",
                    width: "4px",
                    height: "82px",
                    background: "#495057",
                    transformOrigin: "50% 100%",
                    transform: `translateX(-50%) rotate(${angle}deg)`,
                    borderRadius: "999px",
                    boxShadow: "0 0 2px rgba(0,0,0,0.35)",
                    zIndex: 3,
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "-6px",
                    left: "50%",
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    background: "#212529",
                    transform: "translateX(-50%)",
                    boxShadow: "0 0 3px rgba(0,0,0,0.4)",
                    zIndex: 3,
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: "6px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "14px",
                  color: npsValue < 0 ? "#dc3545" : "#28a745",
                }}
              >
                <span style={{ fontWeight: 600 }}>{npsValue}</span>
                <span style={{ fontSize: "11px", color: "#6c757d" }}>Net Promoter Score</span>
              </div>
              <div
                style={{
                  marginTop: "6px",
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: "999px",
                  background: "#adb5bd",
                  fontSize: "11px",
                  color: "#fff",
                }}
              >
                Total Responses: <strong>{total}</strong>
              </div>
            </div>
          </div>
          <div style={{ flex: "1 1 260px", minWidth: "240px" }}>
            <div
              style={{
                marginBottom: "8px",
                height: "16px",
                borderRadius: "999px",
                background: "#e9ecef",
                overflow: "hidden",
                boxShadow: "0 0 0 1px #dee2e6 inset",
              }}
            >
              <div style={{ width: `${Math.max(0, detractorsPct)}%`, height: "100%", background: red, float: "left" }} />
              <div style={{ width: `${Math.max(0, passivesPct)}%`, height: "100%", background: grey, float: "left" }} />
              <div style={{ width: `${Math.max(0, promotersPct)}%`, height: "100%", background: green, float: "left" }} />
            </div>
            <div style={{ fontSize: "12px", color: "#212529", marginBottom: "6px" }}>
              <div style={{ marginBottom: "3px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: green, border: "1px solid #e9ecef" }} />
                <span>Promoters (9–10): <strong>{nb.promoters_pct}%</strong> ({nb.promoters_count})</span>
              </div>
              <div style={{ marginBottom: "3px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: red, border: "1px solid #e9ecef" }} />
                <span>Detractors (0–6): <strong>{nb.detractors_pct}%</strong> ({nb.detractors_count})</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: grey, border: "1px solid #e9ecef" }} />
                <span>Passives (7–8): <strong>{nb.passives_pct}%</strong> ({nb.passives_count})</span>
              </div>
            </div>
            <div style={{ marginTop: "6px", fontSize: "14px", fontWeight: 600, color: "#212529" }}>
              Net Promoter Score: <span>{npsValue}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 6. Open-Ended
  function renderOpenEndedKpis(r) {
    const list = Array.isArray(r.all_response_texts)
      ? r.all_response_texts
      : Array.isArray(r.sample_answers)
      ? r.sample_answers
      : [];
    const sentiments = Array.isArray(r.all_response_sentiments) && r.all_response_sentiments.length === list.length
      ? r.all_response_sentiments
      : null;
    const avgWords = r.avg_word_count;
    const sb = r.sentiment_breakdown;
    const qid = r.question_id;
    const activeFilter = sentimentFilterByQuestionId[qid] || null;

    const setSentimentFilter = (sentiment) => {
      setSentimentFilterByQuestionId((prev) => ({
        ...prev,
        [qid]: prev[qid] === sentiment ? null : sentiment,
      }));
    };

    if (!list.length) {
      return <p style={{ fontSize: "13px", color: "#6c757d" }}>No responses yet.</p>;
    }

    const positivePct = sb && typeof sb.positive_pct === "number" ? sb.positive_pct : 0;
    const neutralPct = sb && typeof sb.neutral_pct === "number" ? sb.neutral_pct : 0;
    const negativePct = sb && typeof sb.negative_pct === "number" ? sb.negative_pct : 0;
    const totalPct = positivePct + neutralPct + negativePct || 1;
    const normalize = (v) => (totalPct > 0 ? (v / totalPct) * 100 : 0);
    const positiveWidth = normalize(positivePct);
    const neutralWidth = normalize(neutralPct);
    const negativeWidth = normalize(negativePct);

    const linkStyle = (sentiment) => ({
      color: "var(--sentiment-label)",
      cursor: "pointer",
      textDecoration: "none",
      background: "none",
      border: "none",
      padding: 0,
      font: "inherit",
      ...(activeFilter === sentiment ? { fontWeight: 700 } : {}),
    });

    const displayList =
      activeFilter && sentiments
        ? list.map((text, i) => ({ text, i, sentiment: sentiments[i] })).filter((x) => x.sentiment === activeFilter)
        : list.map((text, i) => ({ text, i, sentiment: sentiments ? sentiments[i] : null }));

    return (
      <div style={{ marginTop: "6px" }}>
        {sb && sb.total > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "4px",
                fontSize: "12px",
                color: "#212529",
              }}
            >
              <div style={{ fontWeight: 600 }}>Sentiment breakdown</div>
              <div style={{ fontSize: "11px", color: "#6c757d" }}>
                Based on {sb.total} response{sb.total === 1 ? "" : "s"}
              </div>
            </div>
            <div
              style={{
                height: "16px",
                borderRadius: "999px",
                background: "#f1f3f5",
                overflow: "hidden",
                boxShadow: "0 0 0 1px #dee2e6 inset",
              }}
            >
              <div style={{ float: "left", width: `${positiveWidth}%`, height: "100%", background: "var(--sentiment-positive)" }} />
              <div style={{ float: "left", width: `${neutralWidth}%`, height: "100%", background: "var(--sentiment-neutral)" }} />
              <div style={{ float: "left", width: `${negativeWidth}%`, height: "100%", background: "var(--sentiment-negative)" }} />
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                marginTop: "6px",
                fontSize: "11px",
                color: "var(--sentiment-label)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "var(--sentiment-positive)" }} />
                {sentiments ? (
                  <button type="button" style={linkStyle("positive")} onClick={() => setSentimentFilter("positive")}>
                    Positive <strong>{positivePct.toFixed(0)}%{sb.counts && sb.counts.positive ? ` (${sb.counts.positive})` : ""}</strong>
                  </button>
                ) : (
                  <span>Positive <strong>{positivePct.toFixed(0)}%{sb.counts && sb.counts.positive ? ` (${sb.counts.positive})` : ""}</strong></span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "var(--sentiment-neutral)" }} />
                {sentiments ? (
                  <button type="button" style={linkStyle("neutral")} onClick={() => setSentimentFilter("neutral")}>
                    Neutral <strong>{neutralPct.toFixed(0)}%{sb.counts && sb.counts.neutral ? ` (${sb.counts.neutral})` : ""}</strong>
                  </button>
                ) : (
                  <span>Neutral <strong>{neutralPct.toFixed(0)}%{sb.counts && sb.counts.neutral ? ` (${sb.counts.neutral})` : ""}</strong></span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "2px", background: "var(--sentiment-negative)" }} />
                {sentiments ? (
                  <button type="button" style={linkStyle("negative")} onClick={() => setSentimentFilter("negative")}>
                    Negative <strong>{negativePct.toFixed(0)}%{sb.counts && sb.counts.negative ? ` (${sb.counts.negative})` : ""}</strong>
                  </button>
                ) : (
                  <span>Negative <strong>{negativePct.toFixed(0)}%{sb.counts && sb.counts.negative ? ` (${sb.counts.negative})` : ""}</strong></span>
                )}
              </div>
            </div>
          </div>
        )}
        {avgWords != null && (
          <div style={{ fontSize: "13px", color: "#212529", marginBottom: "8px" }}>
            <strong>Average word count:</strong> {avgWords}
          </div>
        )}
        <div style={{ fontSize: "12px", color: "#495057", marginBottom: "4px" }}>
          All responses
          {activeFilter && (
            <>
              {" "}
              <span style={{ color: "#6c757d" }}>(filtered: {activeFilter})</span>
              {" "}
              <button
                type="button"
                onClick={() => setSentimentFilterByQuestionId((prev) => ({ ...prev, [qid]: null }))}
                style={{ color: "var(--sentiment-label)", cursor: "pointer", textDecoration: "none", background: "none", border: "none", padding: 0, font: "inherit", fontSize: "12px", fontWeight: 600 }}
              >
                Show all
              </button>
            </>
          )}
        </div>
        <div
          style={{
            marginTop: "4px",
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "6px 12px",
            fontSize: "13px",
            color: "#212529",
          }}
        >
          {displayList.map((item, idx) => {
            const text = typeof item === "object" && item && "text" in item ? item.text : item;
            const key = typeof item === "object" && item && "i" in item ? item.i : idx;
            return (
              <div key={key} style={{ display: "flex", alignItems: "flex-start", gap: "6px", minWidth: 0 }}>
                <span style={{ fontSize: "10px", lineHeight: "1.4", marginTop: "2px" }}>•</span>
                <span style={{ wordBreak: "break-word" }}>{text}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "8px 0" }}>
        <p style={{ fontSize: "13px", color: "#6c757d" }}>Loading survey results…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "8px 0" }}>
        <p style={{ fontSize: "13px", color: "#842029" }}>{error}</p>
      </div>
    );
  }

  if (!survey) {
    return (
      <div style={{ padding: "8px 0" }}>
        <p style={{ fontSize: "13px", color: "#6c757d" }}>Survey not found.</p>
      </div>
    );
  }

  const wrapperStyle = embedded
    ? { margin: 0, padding: 0 }
    : {
        maxWidth: "900px",
        margin: "24px auto",
        padding: "16px",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      };

  return (
    <div style={wrapperStyle}>
      <div
        style={{
          borderRadius: embedded ? "8px" : "12px",
          border: "1px solid #dee2e6",
          padding: embedded ? "12px 14px" : "16px 18px",
          background: "#f8f9fa",
          marginBottom: embedded ? "0" : "16px",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
        }}
      >
        <div style={{ flex: "1 1 280px", minWidth: 0 }}>
          {!embedded && (
            <h1 style={{ margin: "0 0 6px 0", fontSize: "1.4rem", color: "#212529" }}>
              Survey Results – {survey.name || "Survey"}
            </h1>
          )}
          {!embedded && survey.description && (
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#495057" }}>
              {survey.description}
            </p>
          )}
          <div style={{ marginTop: "6px", fontSize: "12px", color: "#6c757d" }}>
            {survey.start_date && survey.end_date && (
              <span>
                Open from {dayjs(survey.start_date).format("MMM D, YYYY")} to {dayjs(survey.end_date).format("MMM D, YYYY")}.{" "}
              </span>
            )}
            <span>
              Total responses: <strong>{totalResponses}</strong>
            </span>
          </div>
        </div>
        {results.length > 0 && (
          <div style={{ flexShrink: 0 }}>
            <label
              htmlFor="survey-question-filter"
              style={{
                display: "block",
                fontSize: "12px",
                fontWeight: 600,
                color: "#495057",
                marginBottom: "4px",
              }}
            >
              Questions
            </label>
            <select
              id="survey-question-filter"
              multiple
              size={Math.min(6, Math.max(1, results.length))}
              value={questionFilter}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, (o) => o.value);
                setQuestionFilter(selected);
              }}
              style={{
                minWidth: "220px",
                padding: "6px 8px",
                fontSize: "13px",
                borderRadius: "6px",
                border: "1px solid #dee2e6",
                background: "#fff",
                color: "#212529",
              }}
            >
              {results.map((r) => (
                <option key={r.question_id} value={r.question_id}>
                  {r.question_text && r.question_text.length > 50
                    ? r.question_text.slice(0, 50).trim() + "…"
                    : r.question_text || "Question"}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {(() => {
        const filteredResults =
          questionFilter.length === 0
            ? results
            : results.filter((r) => questionFilter.includes(r.question_id));
        if (filteredResults.length === 0) {
          return (
            <p style={{ fontSize: "14px", color: "#6c757d" }}>
              {results.length === 0
                ? "No results to show yet."
                : "No questions match the current filter. Select one or more questions."}
            </p>
          );
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {filteredResults.map((r) => (
              <div
                key={r.question_id}
                style={{
                  borderRadius: "10px",
                  border: "1px solid #dee2e6",
                  padding: "12px 14px",
                  background: "#fff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginBottom: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: 600,
                      color: "#212529",
                      maxWidth: "75%",
                    }}
                  >
                    {r.question_text}
                  </div>
                  <div style={{ fontSize: "11px", color: "#6c757d" }}>
                    {TYPE_LABELS[r.question_type] || r.question_type}
                  </div>
                </div>
                <div style={{ fontSize: "12px", color: "#6c757d", marginBottom: "4px" }}>
                  Answers collected: <strong>{r.total_answers || 0}</strong>
                </div>

                {r.question_type === "single_choice" && renderSingleChoiceKpis(r)}
                {r.question_type === "multi_choice" && renderMultiChoiceKpis(r)}
                {r.question_type === "rating" && renderRatingKpis(r)}
                {r.question_type === "likert" && renderLikertKpis(r)}
                {r.question_type === "nps" && renderNpsKpis(r)}
                {r.question_type === "open_ended" && renderOpenEndedKpis(r)}
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}

export default SurveyResultsContent;
